import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Timer, CheckCircle2, AlertTriangle } from "lucide-react";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { RoleGate } from "@/components/RoleGate";
import { api } from "@/lib/api";

/**
 * RC3 · Auto-Archive settings + preview + scheduler-status panel.
 *
 * Super-admin only (existing `super_admin` role gates the endpoints;
 * the RoleGate here mirrors the same guard so operators without the
 * role see the standard "no access" page).
 *
 * UX contract from the RC3 dispatch:
 *   1. Preview qualifying tours MUST be shown BEFORE the user can
 *      enable. When `enabled=false` we hide the enable toggle until
 *      a preview call has completed successfully; the confirm modal
 *      re-uses the count from that preview.
 *   2. Enabling requires an explicit confirmation modal with the
 *      qualifying count and scheduled time; disabling does not.
 *   3. The panel shows current state + timezone + next scheduled at
 *      + last-run summary.
 */
export default function AutoArchiveSettings() {
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/auto-archive/settings");
      setState(r.data);
    } catch (e) {
      toast.error("Failed to load auto-archive settings");
    }
  };

  useEffect(() => { load(); }, []);

  const runPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.get("/admin/auto-archive/preview-count");
      setPreview(r.data);
    } catch {
      toast.error("Preview failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  const doToggle = async (next) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/auto-archive/settings", { enabled: next });
      setState(r.data);
      toast.success(next ? "Auto-archive enabled" : "Auto-archive disabled");
      setShowConfirm(false);
    } catch {
      toast.error("Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  const onEnableClick = () => {
    if (!preview) {
      toast.error("Run 'Preview qualifying tours' before enabling.");
      return;
    }
    setShowConfirm(true);
  };

  const formatScheduledAt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
        timeZone: state?.timezone || "America/Chicago",
        timeZoneName: "short",
      });
    } catch { return iso; }
  };

  return (
    <RoleGate roles={["super_admin"]}>
      <PageShell>
        <PageHeader
          testid="page-auto-archive-settings"
          eyebrow="Administration"
          title="Auto-Archive"
          subtitle="Scheduled daily cleanup that archives tours after their end date"
        />
        {state === null ? (
          <div className="text-steel text-[13px] py-8" data-testid="auto-archive-loading">
            <Loader2 className="inline animate-spin mr-2" size={14} /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* State card */}
            <section className="bg-white border border-coach-silver rounded-md p-4" data-testid="auto-archive-state-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[13px] font-semibold uppercase tracking-widest text-steel">Status</h3>
                <span
                  data-testid="auto-archive-enabled-badge"
                  className={
                    "text-[10.5px] uppercase tracking-widest px-2 py-0.5 rounded-sm border " +
                    (state.enabled
                      ? "bg-touring-lime/10 border-touring-lime text-carbon"
                      : "bg-white border-coach-silver text-steel")
                  }
                >
                  {state.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              <dl className="text-[12.5px] space-y-2 mt-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={13} className="text-coach-blue" />
                  <dt className="text-steel min-w-[140px]">Timezone</dt>
                  <dd className="text-carbon" data-testid="auto-archive-timezone">{state.timezone}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Timer size={13} className="text-coach-blue" />
                  <dt className="text-steel min-w-[140px]">Next scheduled tick</dt>
                  <dd className="text-carbon" data-testid="auto-archive-next-tick">{formatScheduledAt(state.next_scheduled_at)}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-coach-blue" />
                  <dt className="text-steel min-w-[140px]">Last run started</dt>
                  <dd className="text-carbon" data-testid="auto-archive-last-started">{state.last_run_started_at ? formatScheduledAt(state.last_run_started_at) : "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-coach-blue" />
                  <dt className="text-steel min-w-[140px]">Last run finished</dt>
                  <dd className="text-carbon" data-testid="auto-archive-last-finished">{state.last_run_finished_at ? formatScheduledAt(state.last_run_finished_at) : "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-coach-blue" />
                  <dt className="text-steel min-w-[140px]">Last result</dt>
                  <dd className="text-carbon font-mono text-[11.5px]" data-testid="auto-archive-last-result">{state.last_result || "—"}</dd>
                </div>
              </dl>
            </section>

            {/* Preview + toggle card */}
            <section className="bg-white border border-coach-silver rounded-md p-4" data-testid="auto-archive-preview-card">
              <h3 className="text-[13px] font-semibold uppercase tracking-widest text-steel mb-2">Preview & controls</h3>
              <p className="text-[12.5px] text-steel">
                Before enabling, preview how many tours qualify for auto-archive right now.
              </p>
              <button
                type="button"
                onClick={runPreview}
                disabled={loadingPreview}
                data-testid="auto-archive-preview-btn"
                className="mt-3 px-3 py-1.5 text-[12.5px] rounded-sm border border-coach-blue text-coach-blue hover:bg-coach-blue/10 disabled:opacity-40"
              >
                {loadingPreview ? "Previewing…" : "Preview qualifying tours"}
              </button>

              {preview && (
                <div
                  className="mt-3 border border-coach-silver rounded-sm p-3 text-[12.5px]"
                  data-testid="auto-archive-preview-result"
                >
                  <div className="text-carbon">
                    <span className="font-semibold text-[15px] text-coach-blue" data-testid="auto-archive-preview-count">
                      {preview.qualifying_count}
                    </span>{" "}
                    tour{preview.qualifying_count === 1 ? "" : "s"} would archive as of{" "}
                    <span className="font-mono">{preview.today_business_date}</span> ({preview.timezone}).
                  </div>
                  {preview.tour_ids_sample?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-steel hover:text-carbon">
                        First {preview.tour_ids_sample.length} ids
                      </summary>
                      <ul className="mt-1 text-[11px] font-mono list-disc list-inside text-steel" data-testid="auto-archive-preview-ids">
                        {preview.tour_ids_sample.map((id) => (<li key={id}>{id}</li>))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center gap-2">
                {state.enabled ? (
                  <button
                    type="button"
                    onClick={() => doToggle(false)}
                    disabled={saving}
                    data-testid="auto-archive-disable-btn"
                    className="px-3 py-1.5 text-[12.5px] rounded-sm border border-critical-red text-critical-red hover:bg-critical-red/5 disabled:opacity-40"
                  >
                    Disable auto-archive
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onEnableClick}
                    disabled={saving || !preview}
                    data-testid="auto-archive-enable-btn"
                    className="px-3 py-1.5 text-[12.5px] rounded-sm bg-coach-blue text-white hover:bg-deep-road-navy disabled:opacity-40"
                  >
                    Enable auto-archive
                  </button>
                )}
                {!preview && !state.enabled && (
                  <span className="text-[11.5px] text-steel">Run a preview first to enable.</span>
                )}
              </div>
            </section>
          </div>
        )}

        {showConfirm && preview && (
          <div
            role="dialog" aria-modal="true"
            data-testid="auto-archive-confirm-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3"
            onClick={(e) => { if (e.target === e.currentTarget) setShowConfirm(false); }}
          >
            <div className="bg-white border border-coach-silver rounded-md shadow-lg w-full max-w-md">
              <div className="px-4 py-3 border-b border-coach-silver flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-hold" />
                <h2 className="text-[14px] font-semibold text-carbon">Enable automatic archiving?</h2>
              </div>
              <div className="p-4 text-[13px] text-carbon">
                <p>
                  This will automatically archive completed tours daily at{" "}
                  <span className="font-mono">04:00 {state?.timezone}</span>. Ongoing
                  and in-progress tours are never archived.
                </p>
                <div className="mt-3 border border-coach-silver rounded-sm p-2.5 text-[12.5px] bg-road-white">
                  <span className="font-semibold text-coach-blue text-[15px]">{preview.qualifying_count}</span>{" "}
                  tour{preview.qualifying_count === 1 ? "" : "s"} qualify today ({preview.today_business_date} · {preview.timezone}).
                </div>
              </div>
              <div className="px-4 py-3 border-t border-coach-silver flex justify-end gap-2 bg-road-white">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  data-testid="auto-archive-confirm-cancel"
                  className="px-3 py-1.5 text-[12.5px] border border-coach-silver rounded-sm hover:bg-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => doToggle(true)}
                  disabled={saving}
                  data-testid="auto-archive-confirm-enable"
                  className="px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy disabled:opacity-40"
                >
                  {saving ? "Enabling…" : "Enable auto-archive"}
                </button>
              </div>
            </div>
          </div>
        )}
      </PageShell>
    </RoleGate>
  );
}
